import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ShoppingCart,
  Zap,
  Eye,
  Leaf,
  ScrollText,
  ShoppingBag,
  GraduationCap,
  ArrowRight,
  UserCheck,
  Compass,
  FileText,
  MessageCircle,
  Video,
  Headphones,
  CalendarCheck,
  Smartphone,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { KD9LogoCircle, KD9Watermark } from "@/components/KD9Logo";
import { AcompSection } from "@/pages/Acompanamiento";
import PublishedCatalogs from "@/components/PublishedCatalogs";
import DailyHoroscope from "@/components/DailyHoroscope";
import LiveConsultantsSection from "@/components/LiveConsultantsSection";
import EgyptEntryExperience from "@/components/EgyptEntryExperience";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

/* Approved KD9 final artwork. Keep public landing fallbacks on-brand and non-stock. */
const IMG = Object.freeze({
  heroTemple:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/409c957424fc257d0b4330cfe00ac29c62687ad187c88ec5a4ed19e8c552f580.png",
  diagnostics:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/38b812bf1b127538cf7bc8ffda861781098d6bb1e68e9318f0fe53a6306dc18e.png",
  support:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/3d00f205bfbb4740c5c207f51bda145bfd296713caab979f4cd16b43829a1527.png",
  shop:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/5a941b5461e822aba153d7cb27033977347279f4548c1dae7c7bd5e4cd6c24d7.png",
  academy:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/f1d02a1ed35d413ec4de34af9e9f226cd25b5fa431fd84deb2421c71c35cc1e4.png",
});

const STUDIES = [
  {
    nameKey: "diag.egipcio.name",
    descKey: "diag.egipcio.desc",
    img: IMG.diagnostics,
    icon: "𓂀",
  },
  {
    nameKey: "diag.esencial.name",
    descKey: "diag.esencial.desc",
    img: IMG.diagnostics,
    icon: "☥",
  },
  {
    nameKey: "diag.holistico.name",
    descKey: "diag.holistico.desc",
    img: IMG.diagnostics,
    icon: "🜂",
  },
  {
    nameKey: "diag.terreno.name",
    descKey: "diag.terreno.desc",
    img: IMG.diagnostics,
    icon: "✦",
  },
  {
    nameKey: "diag.maestro.name",
    descKey: "diag.maestro.desc",
    img: IMG.diagnostics,
    icon: "✺",
  },
];

const STEPS = [
  { n: 1, icon: ShoppingCart },
  { n: 2, icon: FileText },
  { n: 3, icon: ScrollText },
  { n: 4, icon: Sparkles },
];

const PROCESS_VALUES = [
  { key: "personalized", icon: UserCheck },
  { key: "professional", icon: Eye },
  { key: "spiritual", icon: Leaf },
  { key: "delivery", icon: ScrollText },
];

const Landing = () => {
  const { t, lang } = useLanguage();
  const preview = new URLSearchParams(window.location.search).get("preview") === "1";
  const immersiveReset = new URLSearchParams(window.location.search).get("immersive") === "1";
  const [landing, setLanding] = useState(null);
  const [landingResolved, setLandingResolved] = useState(false);
  const [classicTarget, setClassicTarget] = useState("");
  const [experienceDismissed, setExperienceDismissed] = useState(
    !preview && !immersiveReset && window.sessionStorage.getItem("kd9-egypt-dismissed") === "1"
  );
  useEffect(()=>{
    let active=true;
    setLandingResolved(false);
    const loadPublic=()=>api.get("/api/landing/public")
      .then(({data})=>{if(active)setLanding(data);})
      .catch(()=>{if(active)setLanding(null);})
      .finally(()=>{if(active)setLandingResolved(true);});
    const request=preview?api.get("/api/director/landing"):api.get("/api/landing/public");
    request
      .then(({data})=>{if(active){setLanding(data);setLandingResolved(true);}})
      .catch(()=>{if(preview){loadPublic();}else if(active){setLanding(null);setLandingResolved(true);}});
    return()=>{active=false;};
  },[preview]);
  useEffect(()=>{
    if(!preview&&immersiveReset){
      window.sessionStorage.removeItem("kd9-egypt-dismissed");
      setExperienceDismissed(false);
    }
  },[immersiveReset,preview]);
  useEffect(()=>{
    if(!experienceDismissed||!classicTarget)return undefined;
    const frame=window.requestAnimationFrame(()=>{
      document.getElementById(classicTarget)?.scrollIntoView({behavior:"smooth",block:"start"});
      setClassicTarget("");
    });
    return()=>window.cancelAnimationFrame(frame);
  },[classicTarget,experienceDismissed]);
  const enabled=(key)=>landing?.sections?.[key]!==false;
  const content=landing?.content?.[lang]||landing?.content?.es||{};
  const media=landing?.media||{};
  const announcement=landing?.announcement||{};
  const nowTime=Date.now();
  const announcementActive=enabled("announcement")&&Boolean(content.announcement_body)&&(!announcement.starts_at||new Date(announcement.starts_at).getTime()<=nowTime)&&(!announcement.ends_at||new Date(announcement.ends_at).getTime()>=nowTime);
  const experience=landing?.experience||{};
  if(!landingResolved){
    return (
      <div className="min-h-screen grid place-items-center bg-[#020305]" data-testid="landing-config-loading">
        <div className="font-cinzel text-xs tracking-[.28em] kd9-gold-text opacity-70">KHAOS DELTA 9</div>
      </div>
    );
  }
  if(experience.enabled&&!experienceDismissed){
    return <EgyptEntryExperience config={experience} previewMode={preview} onExit={(options={})=>{
      const target=typeof options==="string"?options:(typeof options?.target==="string"?options.target:"");
      const persist=options&&typeof options==="object"&&"persist" in options?options.persist!==false:true;
      if(!preview&&persist) window.sessionStorage.setItem("kd9-egypt-dismissed","1");
      if(target)setClassicTarget(target);
      setExperienceDismissed(true);
    }} />;
  }
  return (
    <div className="min-h-screen" data-testid="landing-page">
      {experience.enabled && experienceDismissed && (
        <button
          type="button"
          onClick={() => {
            if(!preview) window.sessionStorage.removeItem("kd9-egypt-dismissed");
            setExperienceDismissed(false);
          }}
          className="fixed right-4 bottom-4 z-[100] kd9-btn-gold px-4 py-3 rounded-xl text-[10px] shadow-2xl"
          data-testid={preview?"egypt-preview-return":"egypt-public-return"}
        >
          {preview?"VOLVER A EXPERIENCIA EGIPCIA":"EXPLORAR TERRITORIO"}
        </button>
      )}
      <PublicNav />

      {/* ============================ HERO ============================ */}
      <section hidden={!enabled("hero")} className="relative overflow-hidden">
        {/* background imagery */}
        <div className="absolute inset-0 z-0">
          <img
            src={media.hero_image_url||IMG.heroTemple}
            alt=""
            className="w-full h-full object-cover opacity-50"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, var(--kd9-overlay) 0%, var(--kd9-overlay) 60%, var(--kd9-bg) 100%), radial-gradient(circle at 70% 30%, var(--kd9-glow), transparent 55%)",
            }}
          />
          <KD9Watermark
            size={780}
            className="-right-32 -top-20"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 kd9-fade-up">
            <div className="flex items-center gap-3 mb-8">
              <KD9LogoCircle size={64} />
              <div
                className="text-[11px] font-mont tracking-[0.45em] uppercase"
                style={{ color: "var(--kd9-gold)" }}
              >
                {t("hero.kicker")}
              </div>
            </div>

            <h1
              className="font-cinzel kd9-gold-text leading-[0.95] mb-6"
              style={{
                fontSize: "clamp(3rem, 7vw, 6.5rem)",
                fontWeight: 800,
                letterSpacing: "0.02em",
              }}
            >
              {(content.hero_title||t("hero.title")).split(" DELTA ").map((part, index) => <React.Fragment key={part}>{index ? <><br />DELTA </> : null}{part}</React.Fragment>)}
            </h1>

            <p
              className="font-cormorant italic text-2xl md:text-3xl mb-6"
              style={{ color: "var(--kd9-text)", letterSpacing: "0.02em" }}
            >
              {content.hero_tagline||t("hero.tagline")}
            </p>

            <p
              className="font-mont text-base md:text-lg max-w-2xl mb-10 leading-relaxed"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              {content.hero_subtitle||t("hero.subtitle")}
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/register"
                className="kd9-btn-gold px-7 py-4 rounded-xl text-[12px] flex items-center gap-3"
                data-testid="hero-cta-solicitar"
              >
                <ScrollText size={16} /> {content.hero_primary_cta||t("hero.cta.solicitar")}
              </Link>
              <Link
                to="/login"
                className="kd9-btn-red px-7 py-4 rounded-xl text-[12px] flex items-center gap-3"
                data-testid="hero-cta-consulta"
              >
                <Zap size={16} /> {content.hero_secondary_cta||t("hero.cta.consulta")}
              </Link>
              <Link
                to="/tienda"
                className="kd9-btn-outline px-7 py-4 rounded-xl text-[12px] flex items-center gap-3"
                data-testid="hero-cta-tienda"
              >
                <ShoppingCart size={16} /> {t("hero.cta.tienda")}
              </Link>
              <div className="flex flex-col gap-2">
                <Link
                  to="/descarga"
                  className="kd9-btn-outline px-7 py-4 rounded-xl text-[12px] flex items-center gap-3"
                  data-testid="hero-cta-download-app"
                >
                  <Smartphone size={16} /> Descargar KD9 para Android
                </Link>
                <Link
                  to="/descarga#iphone"
                  className="kd9-btn-outline px-7 py-4 rounded-xl text-[12px] flex items-center gap-3"
                  data-testid="hero-cta-download-iphone"
                >
                  <Smartphone size={16} /> Descargar KD9 para iPhone
                </Link>
              </div>
            </div>

            <div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-[11px] font-mont tracking-[0.32em] uppercase"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              <span className="flex items-center gap-2"><Compass size={12} style={{ color: "var(--kd9-gold)" }} /> {t("landing.badge.geometry")}</span>
              <span className="flex items-center gap-2"><Eye size={12} style={{ color: "var(--kd9-gold)" }} /> {t("landing.badge.vision")}</span>
              <span className="flex items-center gap-2"><Leaf size={12} style={{ color: "var(--kd9-gold)" }} /> {t("landing.badge.nature")}</span>
            </div>
          </div>

          {/* hero portal — official KD9 logo */}
          <div className="lg:col-span-5 relative h-[460px] lg:h-[620px] hidden lg:block">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative kd9-pulse">
                <KD9LogoCircle size={520} />
              </div>
              {/* portal glow door */}
              <div
                className="absolute left-1/2 bottom-10 -translate-x-1/2 w-[140px] h-[260px] rounded-t-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(255,213,74,0.45), rgba(212,175,55,0.0) 70%)",
                  filter: "blur(8px)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <div id="horoscopo-diario" className="scroll-mt-6">
        <DailyHoroscope />
      </div>
      <LiveConsultantsSection />

      {announcementActive&&<section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-10" data-testid="landing-announcement"><div className="kd9-card overflow-hidden grid md:grid-cols-[1fr_auto] gap-5 items-center p-6">{announcement.image_url&&<img src={announcement.image_url} alt="" className="w-full md:w-48 h-32 object-cover rounded-xl md:order-2"/>}<div><div className="font-cinzel kd9-gold-text text-2xl mb-2">{content.announcement_title}</div><p className="font-cormorant text-xl whitespace-pre-line" style={{color:"var(--kd9-text-soft)"}}>{content.announcement_body}</p>{announcement.cta_url&&<Link to={announcement.cta_url} className="kd9-btn-outline px-4 py-2 rounded-lg text-xs inline-flex mt-4">{content.announcement_cta}</Link>}</div></div></section>}

      <PublishedCatalogs config={landing} />

      {/* ============================ PROCESS VALUES ============================ */}
      <section hidden={!enabled("process")} className="relative max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <h2 className="font-cinzel text-center text-xl md:text-2xl tracking-[0.18em] kd9-gold-text mb-12">
          MÁS QUE UNA CONSULTA, ES UN PROCESO DE CLARIDAD
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS_VALUES.map((v) => (
            <div
              key={t(`landing.process.${v.key}`)}
              className="kd9-card p-6 text-center"
              data-testid={`process-value-${v.key}`}
            >
              <div
                className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{
                  background: "rgba(212,175,55,0.08)",
                  border: "1px solid var(--kd9-border)",
                }}
              >
                <v.icon size={22} style={{ color: "var(--kd9-gold)" }} />
              </div>
              <div className="font-mont text-sm tracking-wider">{t(`landing.process.${v.key}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================ HOW IT WORKS ============================ */}
      <section hidden={!enabled("how_it_works")} className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="section-how">
        <SectionTitle>{t("section.how")}</SectionTitle>

        <div className="mt-14 relative">
          <div
            className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent)",
            }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step) => (
              <div
                key={step.n}
                className="relative text-center kd9-fade-up"
                data-testid={`step-${step.n}`}
              >
                <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-5">
                  <div
                    className="absolute inset-0 rounded-full kd9-pulse"
                    style={{
                      background:
                        "radial-gradient(circle, rgba(212,175,55,0.22), transparent 70%)",
                    }}
                  />
                  <div
                    className="relative w-20 h-20 rounded-full flex items-center justify-center font-cinzel text-2xl kd9-gold-text border"
                    style={{
                      borderColor: "var(--kd9-gold)",
                      background: "var(--kd9-bg-2)",
                    }}
                  >
                    {step.n}
                  </div>
                </div>
                <step.icon size={20} className="mx-auto mb-3" style={{ color: "var(--kd9-gold)" }} />
                <h4 className="font-cinzel text-base tracking-[0.14em] mb-2 kd9-gold-text">
                  {t(`landing.step.${step.n}.title`)}
                </h4>
                <p
                  className="font-cormorant text-base leading-snug"
                  style={{ color: "var(--kd9-text-soft)" }}
                >
                  {t(`landing.step.${step.n}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ ¿QUÉ ES KHAOS DELTA 9? ============================ */}
      <section hidden={!enabled("about")} className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="section-whatis">
        <div className="text-center mb-14">
          <div
            className="font-mont text-[11px] tracking-[0.45em] uppercase mb-4"
            style={{ color: "var(--kd9-accent)" }}
          >
            · {t("section.whatis")} ·
          </div>
          <h2 className="font-cinzel kd9-gold-text text-3xl md:text-5xl tracking-[0.06em] kd9-arrow inline-flex items-center mb-3">
            {t("section.whatis").toUpperCase()}
          </h2>
          <p
            className="font-cormorant italic text-2xl mt-4"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            &ldquo;{t("whatis.message")}&rdquo;
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { titleKey: "whatis.khaos.title", descKey: "whatis.khaos.desc", accent: "#A67C00", glyph: "K" },
            { titleKey: "whatis.delta.title", descKey: "whatis.delta.desc", accent: "#D4AF37", glyph: "Δ" },
            { titleKey: "whatis.nine.title", descKey: "whatis.nine.desc", accent: "#FFD54A", glyph: "9" },
          ].map((c, i) => (
            <div
              key={c.titleKey}
              className={`kd9-card p-10 text-center kd9-fade-up kd9-delay-${i + 1}`}
              data-testid={`whatis-${c.titleKey}`}
            >
              <div
                className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6 font-cinzel"
                style={{
                  fontSize: 52,
                  fontWeight: 700,
                  color: c.accent,
                  textShadow: `0 0 18px ${c.accent}99`,
                  background: `${c.accent}15`,
                  border: `1px solid ${c.accent}66`,
                  boxShadow: `0 0 32px -8px ${c.accent}66`,
                }}
              >
                {c.glyph}
              </div>
              <h3
                className="font-cinzel kd9-gold-text text-3xl tracking-[0.16em] mb-3"
              >
                {t(c.titleKey)}
              </h3>
              <div
                className="kd9-divider w-12 mx-auto mb-4"
              />
              <p
                className="font-cormorant text-xl"
                style={{ color: "var(--kd9-text-soft)" }}
              >
                {t(c.descKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================ ACOMPAÑAMIENTO (PRIORIDAD VISUAL) ============================ */}
      {enabled("accompaniment")&&<AcompSection
        embedded
        t={t}
        options={[
          { key: "hablar", icon: MessageCircle, color: "#1FA774", img: IMG.support },
          { key: "video", icon: Video, color: "#3C78B5", img: IMG.support },
          { key: "escuchado", icon: Headphones, color: "#A67C00", img: IMG.support },
          { key: "agendar", icon: CalendarCheck, color: "#D4AF37", img: IMG.support },
        ]}
      />}

      {/* ============================ ACADEMIA + TIENDA ============================ */}
      <section hidden={!enabled("academy_feature")&&!enabled("shop_feature")} className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-8">
        {enabled("academy_feature")&&<FeatureBlock
          title={content.academy_title||t("academia.title").toUpperCase()}
          subtitle={content.academy_subtitle||t("landing.academy.subtitle")}
          img={media.academy_image_url||IMG.academy}
          icon={GraduationCap}
          ctaText={content.academy_cta||t("academia.cta")}
          to="/academia"
          dataTestId="feature-academia"
        />}
        {enabled("shop_feature")&&<FeatureBlock
          title={content.shop_title||t("landing.shop.title")}
          subtitle={content.shop_subtitle||t("landing.shop.subtitle")}
          img={media.shop_image_url||IMG.shop}
          icon={ShoppingBag}
          ctaText={content.shop_cta||t("landing.shop.cta")}
          to="/tienda"
          dataTestId="feature-tienda"
        />}
      </section>

      {/* ============================ FINAL CTA ============================ */}
      <section hidden={!enabled("final_cta")} className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div
          className="relative overflow-hidden rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-center gap-8"
          style={{
            background: "var(--kd9-card)",
            border: "1px solid var(--kd9-border)",
          }}
          data-testid="final-cta"
        >
          <KD9Watermark size={520} opacity={0.05} className="absolute -right-20 -bottom-20" />
          <div className="relative z-10 flex items-center gap-6">
            <KD9LogoCircle size={120} />
            <div>
              <h3 className="font-cinzel text-3xl md:text-4xl kd9-gold-text leading-tight">
                {(content.final_title||t("landing.final")).split("|").map((line) => <React.Fragment key={line}>{line}<br /></React.Fragment>)}
              </h3>
            </div>
          </div>
          <Link
            to="/register"
            className="relative z-10 kd9-btn-gold px-8 py-4 rounded-xl text-[12px] lg:ml-auto flex items-center gap-3"
            data-testid="final-cta-btn"
          >
            {content.final_cta||t("landing.create")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {enabled("footer")&&<Footer />}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <div className="text-center">
    <h2 className="font-cinzel kd9-gold-text text-2xl md:text-4xl tracking-[0.18em] kd9-arrow inline-flex items-center">
      {children.toString().toUpperCase()}
    </h2>
  </div>
);

const FeatureBlock = ({ title, subtitle, img, icon: Icon, ctaText, to, dataTestId }) => (
  <div
    className="relative overflow-hidden rounded-2xl min-h-[280px] flex"
    style={{
      background: "var(--kd9-card)",
      border: "1px solid var(--kd9-border)",
    }}
    data-testid={dataTestId}
  >
    <div className="absolute inset-0">
      <img src={img} alt={title} className="w-full h-full object-cover" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.7) 50%, rgba(5,5,5,0.3) 100%)",
        }}
      />
    </div>
    <div className="relative z-10 p-8 lg:p-10 flex flex-col justify-center max-w-[55%]">
      <Icon size={24} style={{ color: "var(--kd9-gold)" }} className="mb-4" />
      <h3 className="font-cinzel text-2xl md:text-3xl kd9-gold-text tracking-[0.14em] mb-3">{title}</h3>
      <p
        className="font-cormorant text-lg mb-6"
        style={{ color: "var(--kd9-text-soft)" }}
      >
        {subtitle}
      </p>
      <Link
        to={to}
        className="kd9-btn-outline px-5 py-2.5 rounded-lg text-[11px] self-start"
      >
        {ctaText}
      </Link>
    </div>
  </div>
);

export default Landing;
